import { apiClient, postSearch } from "@/shared";
import { createRequestInfo } from "@/shared/api/request-info";
import type { AuthUser } from "@/shared/stores/auth-store";
import type { GeographyDetails } from "../types/project";
import type {
  GeographyLocksResponse,
  GeographyLockTarget,
  InstallationPlan,
  InstallationPlanSearchCriteria,
  InstallationPlanSearchResult,
  ReviewerAssignment,
} from "../types/installation-plan";
import { resolveStates } from "../utils/geography";

const INSTALLATION_ACTIVITY_CODE = "INS";
const INSTALLATION_REVIEWER_ROLE = "INSTALLATION_REPORT_APPROVER_QC_TEAM";

/** `field-planner`'s `geographyDetails` is flat (`{states: string[], districts: string[],
 *  blocks: string[]}`), matching the project's own structured, multi-state shape in cardinality
 *  now that the backend accepts more than one state per plan. */
function toFieldPlanGeography(geography: GeographyDetails) {
  return {
    states: resolveStates(geography).map((state) => state.code),
    districts: (geography.districts ?? []).map((district) => district.code),
    blocks: (geography.blocks ?? []).map((block) => block.code),
  };
}

/** Best-effort reverse mapping for display once a plan is read back — lossy (which state a given
 *  district/block belongs to can't be recovered from the flat list once a plan spans more than
 *  one), but plan geography is read-only after creation so this only ever feeds a disabled view;
 *  `stateCode`/`districtCode` are left blank rather than guessed. */
function fromFieldPlanGeography(flat: { states?: string[]; districts?: string[]; blocks?: string[] }): GeographyDetails {
  return {
    states: (flat.states ?? []).map((code) => ({ code })),
    districts: (flat.districts ?? []).map((code) => ({ code, stateCode: "" })),
    blocks: (flat.blocks ?? []).map((code) => ({ code, districtCode: "", stateCode: "" })),
  };
}

interface RawFieldPlan {
  id?: string;
  tenantId: string;
  projectId: string;
  name?: string;
  sectors?: string[];
  healthFacilityNumber?: number;
  startDate?: number;
  endDate?: number;
  geographyDetails?: { states?: string[]; districts?: string[]; blocks?: string[] };
  status?: string;
}

function toInstallationPlan(raw: RawFieldPlan): InstallationPlan {
  return {
    id: raw.id,
    tenantId: raw.tenantId,
    projectId: raw.projectId,
    name: raw.name,
    startDate: raw.startDate,
    endDate: raw.endDate,
    geographyDetails: fromFieldPlanGeography(raw.geographyDetails ?? {}),
    additionalDetails: {
      sectorCodes: raw.sectors,
      status: raw.status,
    },
  };
}

/** A saved plan, plus whatever went wrong *after* it was stored and so must not fail the save. */
export interface SavedInstallationPlan {
  plan: InstallationPlan;
  /** Set when the plan exists but its reviewer could not be assigned. The plan id is still
   *  usable; the assignment is retried by the next `updateInstallationPlan`. */
  reviewerError?: unknown;
}

/**
 * `POST /field-planner/v1/field-plans/_create` — writes synchronously (unlike `project`), so its
 * response is trustworthy without a follow-up poll.
 */
export async function createInstallationPlan(
  plan: InstallationPlan,
  accessToken?: string,
  user?: AuthUser | null,
): Promise<SavedInstallationPlan> {
  const { data } = await apiClient.post<{ FieldPlans?: RawFieldPlan[] }>(
    "/field-planner/v1/field-plans/_create",
    {
      RequestInfo: createRequestInfo(accessToken, user),
      FieldPlans: [
        {
          tenantId: plan.tenantId,
          projectId: plan.projectId,
          sectors: plan.additionalDetails?.sectorCodes,
          // Not validated against the sites eventually in scope — descriptive only, per the
          // proven backend behavior.
          healthFacilityNumber: 1,
          startDate: plan.startDate,
          endDate: plan.endDate,
          geographyDetails: toFieldPlanGeography(plan.geographyDetails ?? {}),
          activities: [{ code: INSTALLATION_ACTIVITY_CODE, name: "Installation" }],
        },
      ],
    },
  );

  const created = data.FieldPlans?.[0];
  if (!created?.id) throw new Error("INSTALLATION_PLAN_CREATE_FAILED");

  // The plan is already stored by this point, so a failure from here on must not be thrown past
  // the caller: it would lose `created.id`, and the next Next click would take the `_create`
  // branch again and make a second plan, leaving this one orphaned with no reviewer. The failure
  // is reported alongside the plan instead, and `updateInstallationPlan` retries the assignment.
  let reviewerError: unknown;
  if (plan.additionalDetails?.reviewerCode) {
    try {
      await assignInstallationReviewer(
        created.id,
        plan.additionalDetails.reviewerCode,
        plan.startDate,
        plan.endDate,
        accessToken,
        user,
      );
    } catch (error) {
      reviewerError = error;
    }
  }

  return { plan: toInstallationPlan(created), reviewerError };
}

/**
 * `POST /activity/v1/activities/_assign-activity` — the reviewer assignment made alongside plan
 * creation. `activityId` is matched as the string code `"INS"`, not a UUID, despite the field
 * name. The role code is genuinely `INSTALLATION_REPORT_APPROVER_QC_TEAM` (a proven trap — the
 * backend's own constant is misleadingly named after the screen wording).
 *
 * The assignment's own `startDate`/`endDate` must fall within the field plan's date range and be
 * at least a day apart (`INVALID_DATE`/`FIELDPLAN_ENDDATE` otherwise) — using `Date.now()` for
 * both was a proven trap (zero difference, and "now" can be later than a plan's own end date).
 * Falls back to `Date.now()`/`Date.now() + 1 day` only if the plan is somehow missing its own
 * dates (shouldn't happen — both are required by `isPlanDetailsValid`).
 */
export async function assignInstallationReviewer(
  fieldPlanId: string,
  reviewerUuid: string,
  planStartDate?: number,
  planEndDate?: number,
  accessToken?: string,
  user?: AuthUser | null,
): Promise<void> {
  const startDate = planStartDate ?? Date.now();
  const endDate = planEndDate ?? startDate + 24 * 60 * 60 * 1000;

  await apiClient.post("/activity/v1/activities/_assign-activity", {
    RequestInfo: createRequestInfo(accessToken, user),
    ActivityAssignment: [
      {
        tenantId: user?.tenantId,
        fieldPlanId,
        activityId: INSTALLATION_ACTIVITY_CODE,
        activityCode: INSTALLATION_ACTIVITY_CODE,
        activityName: "Installation",
        assignedTo: reviewerUuid,
        assignedBy: user?.uuid,
        role: { code: INSTALLATION_REVIEWER_ROLE, name: "Installation Report Approver QC Team" },
        startDate,
        endDate,
        status: "ACTIVE",
        isDeleted: false,
        // No point-of-contact number is collected in the wizard today — needs a follow-up UI
        // field if the backend starts validating this instead of just storing it.
        pocNumber: "",
      },
    ],
  });
}

/**
 * `POST /activity/v1/activities/assignment/_search` — reads back the reviewer assigned to a plan.
 * The reviewer is never part of `field-planner`'s own `FieldPlan` response (it lives in this
 * service's `activity_assignments` table instead), so re-opening an existing plan needs this
 * separate call or the "Assign Installation Reviewer" field renders as unset even though a
 * reviewer was already assigned at creation. Body key is the singular `ActivityAssignment`
 * (search criteria, not a list) despite the same key naming the array on `_assign-activity`.
 * `tenantId`/`limit`/`offset` must also be query params — the endpoint binds them via
 * `@ModelAttribute URLParams`, so they 400 with `NotNull.URLParams.*` if only sent in the body.
 */
export async function searchAssignedReviewer(
  fieldPlanId: string,
  accessToken?: string,
  user?: AuthUser | null,
): Promise<ReviewerAssignment | undefined> {
  const data = await postSearch<{ ActivityAssignment?: ReviewerAssignment[] }>(
    "/activity/v1/activities/assignment/_search",
    "ActivityAssignment",
    { fieldPlanIds: [fieldPlanId], roles: [INSTALLATION_REVIEWER_ROLE], tenantId: user?.tenantId },
    { accessToken, user },
  );

  return data.ActivityAssignment?.find((assignment) => !assignment.isDeleted && assignment.assignedTo);
}

/**
 * `POST /activity/v1/activities/assignment/_update` — hands a plan's existing reviewer assignment
 * to another reviewer. Sends the stored row back whole with only `assignedTo` changed: the
 * update persister writes every column, and the backend rejects a changed id/tenant/activity/plan.
 * The backend also refuses it once the plan is published (`REVIEWER_LOCKED_AFTER_PUBLISH`).
 */
export async function updateReviewerAssignment(
  assignment: ReviewerAssignment,
  reviewerUuid: string,
  accessToken?: string,
  user?: AuthUser | null,
): Promise<void> {
  await apiClient.post(
    "/activity/v1/activities/assignment/_update",
    {
      RequestInfo: createRequestInfo(accessToken, user),
      ActivityAssignment: [{ ...assignment, assignedTo: reviewerUuid }],
    },
    { params: { tenantId: assignment.tenantId ?? user?.tenantId } },
  );
}

/** `POST /field-planner/v1/field-plans/_update` */
export async function updateInstallationPlan(
  plan: InstallationPlan,
  accessToken?: string,
  user?: AuthUser | null,
): Promise<SavedInstallationPlan> {
  const { data } = await apiClient.post<{ FieldPlans?: RawFieldPlan[] }>(
    "/field-planner/v1/field-plans/_update",
    {
      RequestInfo: createRequestInfo(accessToken, user),
      FieldPlans: [
        {
          id: plan.id,
          tenantId: plan.tenantId,
          projectId: plan.projectId,
          sectors: plan.additionalDetails?.sectorCodes,
          startDate: plan.startDate,
          endDate: plan.endDate,
          geographyDetails: toFieldPlanGeography(plan.geographyDetails ?? {}),
          // Required or `_update` 400s with `{"code":"ACTIVITIES","message":"Activity is
          // mandatory"}` — proven live. A plan only ever has the one fixed Installation
          // activity, the same value `_create` already sends.
          activities: [{ code: INSTALLATION_ACTIVITY_CODE, name: "Installation" }],
        },
      ],
    },
  );

  // The reviewer lives in activity_assignments, which `_update` does not touch. With none assigned
  // (the assignment failed at creation) one is added; a different one chosen on a draft plan
  // replaces the existing assignment, so a plan only ever has one reviewer. The same reviewer is
  // left alone, so this is safe to run on every update.
  let reviewerError: unknown;
  const reviewerCode = plan.additionalDetails?.reviewerCode;
  if (reviewerCode && plan.id) {
    try {
      const assigned = await searchAssignedReviewer(plan.id, accessToken, user);
      if (!assigned) {
        await assignInstallationReviewer(
          plan.id, reviewerCode, plan.startDate, plan.endDate, accessToken, user,
        );
      } else if (assigned.assignedTo !== reviewerCode) {
        await updateReviewerAssignment(assigned, reviewerCode, accessToken, user);
      }
    } catch (error) {
      reviewerError = error;
    }
  }

  const updated = data.FieldPlans?.[0];
  return { plan: updated ? toInstallationPlan(updated) : plan, reviewerError };
}

/**
 * `POST /field-planner/v1/field-plans/_search` — `tenantId` must be inside the `FieldPlans` body,
 * not just the query string (a proven trap: query-string-only 400s with `TENANT_ID: Tenant ID is
 * mandatory`).
 */
export async function searchInstallationPlans(
  {
    criteria,
    limit = 10,
    offset = 0,
  }: {
    criteria?: InstallationPlanSearchCriteria;
    limit?: number;
    offset?: number;
  },
  accessToken?: string,
  user?: AuthUser | null,
): Promise<InstallationPlanSearchResult> {
  const data = await postSearch<{ FieldPlans?: RawFieldPlan[]; TotalCount?: number }>(
    "/field-planner/v1/field-plans/_search",
    "FieldPlans",
    {
      tenantId: user?.tenantId,
      ...(criteria?.id?.length ? { ids: criteria.id } : {}),
      ...(criteria?.projectId ? { projectIds: [criteria.projectId] } : {}),
    },
    { accessToken, user, limit, offset },
  );

  const plans = (data.FieldPlans ?? []).map(toInstallationPlan);

  return {
    plans: plans.map((plan) => ({ plan, status: plan.additionalDetails?.status ?? "DRAFT" })),
    totalCount: data.TotalCount ?? plans.length,
  };
}

/**
 * `POST /activity/v1/vendor-assignment/_create` — the one-shot "Confirm & Submit" action on
 * Technician Assignment. Moves the plan out of "DRAFT" into "PUBLISHED"; **irreversible**, no
 * unpublish endpoint exists. `RequestInfo.userInfo.roles` must be the real roles list or this NPEs
 * deep in egov-workflow-v2. Give this call a generous timeout — the backend's internal
 * transition-polling can take a few seconds under its own retry/backoff.
 */
export async function publishInstallationPlan(
  planId: string,
  assignments: Array<{
    facilityId: string;
    componentType: "SOLAR" | "MACHINE";
    componentSequence: number;
    vendorOrgId?: string;
    vendorOrgName?: string;
    vendorUserId?: string;
  }>,
  accessToken?: string,
  user?: AuthUser | null,
): Promise<InstallationPlan> {
  const { data } = await apiClient.post<{
    fieldPlanId?: string;
    planStatus?: string;
  }>(
    "/activity/v1/vendor-assignment/_create",
    {
      RequestInfo: createRequestInfo(accessToken, user),
      VendorAssignment: { tenantId: user?.tenantId, fieldPlanId: planId },
      Assignments: assignments,
    },
    { timeout: 20_000 },
  );

  // Publishing can't be undone, so the response is checked rather than defaulted. `planStatus`
  // is a real top-level field on VendorAssignmentCreateResponse (confirmed against the service's
  // own model), so its absence means the call did not do what this function claims — defaulting
  // it to "PUBLISHED" would report success to the PM on the one action they can't take back.
  if (!data.planStatus) throw new Error("INSTALLATION_PLAN_PUBLISH_UNCONFIRMED");

  return {
    id: data.fieldPlanId ?? planId,
    tenantId: user?.tenantId ?? "",
    projectId: "",
    additionalDetails: { status: data.planStatus },
  };
}

/**
 * `POST /field-planner/v1/field-plans/geography/_locks` -- the geography codes that cannot be
 * removed: for a project, every code its installation plans use; for a plan, every code with an
 * end-user site in its scope. field-planner owns the rule and enforces it again on save.
 */
export async function fetchGeographyLocks(
  target: GeographyLockTarget,
  accessToken?: string,
  user?: AuthUser | null,
): Promise<GeographyLocksResponse> {
  // The gateway refuses a request carrying no tenantId at all ("Failed to parse request at API
  // gateway", 401) -- this body has none of its own, so it goes on the query string.
  const { data } = await apiClient.post<GeographyLocksResponse>(
    "/field-planner/v1/field-plans/geography/_locks",
    { RequestInfo: createRequestInfo(accessToken, user), ...target },
    { params: { tenantId: user?.tenantId } },
  );
  return data;
}
